import { useState, useEffect } from 'react';
import { Stack } from '@mui/material';
import { Typography, IconButton, colors, Button, Tooltip } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import EditableCardLayout from '../../common/editable-card/EditableCardLayout';
import EditTextarea from '../../common/editable-card/EditTextarea';
import { usePermissions } from '../../../permissions/usePermissions';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { CrmOrgPermission } from '../../../permissions/permissions.types';

const AgentInstructions = ({
  onChange,
  value,
  placeholder,
  closeDialog,
  onPristineStateChange,
  title,
  subtitle,
  permission,
}: {
  onChange: (instructions: string) => Promise<void>;
  value: string;
  placeholder: string;
  closeDialog: () => void;
  onPristineStateChange?: (isPristine: boolean) => void;
  title: string;
  subtitle: string;
  permission: CrmOrgPermission;
}) => {
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const [text, setText] = useState(value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { evaluatePermissions } = usePermissions();
  const [isAllowedToEditAgent] = evaluatePermissions([{ permission, crmOrgId: crmOrgId ?? '' }]);

  const isButtonsDisabled = isSubmitting || text === value || !isAllowedToEditAgent;

  useEffect(() => {
    const isPristine = text === value;
    onPristineStateChange?.(isPristine);
  }, [text, value, onPristineStateChange]);

  const onCancel = () => setText(value);

  const onSave = async () => {
    if (!isAllowedToEditAgent) return;
    setIsSubmitting(true);
    await onChange(text);
    setIsSubmitting(false);
  };

  const textJsx = (
    <EditTextarea
      setText={(newText: string) => (isAllowedToEditAgent ? setText(newText) : undefined)}
      text={text}
      placeholder={placeholder}
    />
  );

  const updateCardJsx = (
    <Stack direction="row" gap={1} width="100%" justifyContent="flex-end">
      <Button variant="system" size="tiny" onClick={onCancel} disabled={isButtonsDisabled}>
        Cancel
      </Button>
      <Tooltip
        title={
          !isAllowedToEditAgent
            ? `To edit the agent's ${title.toLowerCase()}, please contact your admin`
            : undefined
        }
      >
        <span>
          <Button
            variant="primary"
            size="tiny"
            onClick={onSave}
            loading={isSubmitting}
            disabled={isButtonsDisabled}
          >
            Save
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );

  return (
    <Stack gap={2} p="16px 0" overflow="hidden">
      <Stack p="0 16px" gap={2}>
        <Stack
          direction="row"
          sx={{
            height: '32px',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h3">{title}</Typography>

          <IconButton onClick={closeDialog} variant="flat" size="small">
            <Close />
          </IconButton>
        </Stack>
        <Stack maxWidth="75%">
          <Typography variant="caption" color={colors.grey[700]}>
            {subtitle}
          </Typography>
        </Stack>
      </Stack>
      <Stack flex={1} overflow="auto" p="0 16px">
        <EditableCardLayout
          updateCardActionsJsx={updateCardJsx}
          textJsx={textJsx}
          disabled={!isAllowedToEditAgent}
        />
      </Stack>
    </Stack>
  );
};

export default AgentInstructions;
